import { useState } from 'react'
import { useHistory } from 'react-router-dom'
import QRCode from 'react-qr-code'
import buildQuestionnaireShareLinks from '../../../helpers/questionnaires/buildQuestionnaireShareLinks'
import { getQrcodeValue } from '../../../pages/measurementPlan/qrcodeHelpers'
import CopyLink from '../../common/CopyLink'
import Tooltip from '../../common/Tooltip'
import QrcodeIcon from '../../../images/icons/qrcode-icon.png'
import languages from '../../../utils/listOfLanguages'

/**
 * De vragenlijst van het PROGRAMMA bij dit huishouden.
 *
 * WAAROM DIT ER IS. Een lid heeft het onderzoek van de koepel niet in zijn eigen
 * omgeving staan — onderzoeken zijn per organisatie afgeschermd. Op 10 september
 * moest de link daarom met de hand samengesteld worden. De deelname-rij draagt
 * nu de ID's én de namen, dus het scherm kan hem zelf bouwen.
 *
 * DE OMGEVING IN DE LINK IS DIE VAN HET PROGRAMMA. Staat daar de eigen tenant,
 * dan zoekt de invulpagina de vragenlijst in de verkeerde omgeving en vindt hem
 * niet.
 *
 * HET MEETMOMENT WORDT NIET GEKOZEN. Het staat op de rij, gezet door het
 * programma. De klusser aan de deur hoort niet uit een lijst te kiezen welke
 * meting hij afneemt.
 *
 * AI-CONTEXT: See docs/features/energiearmoede-arnhem-ketensamenwerking.md
 *
 * @param {object} props
 * @param {string} props.adressID - `Adresses.ID`; wordt de respondent van de meting
 * @param {Array<object>} [props.participations] - Eigen deelname-rijen; `undefined` = nog niet geladen
 * @param {'page'|'inline'} [props.qrMode] - Waar de QR-code terechtkomt; zie HouseholdResearchLinks
 */
const ProgrammeResearchLink = ({ adressID, participations, qrMode }) => {
    const history = useHistory()

    // Per programma een eigen taalkeuze; de sleutel is het programma.
    const [languageByProgramme, setLanguageByProgramme] = useState({})

    const bruikbaar = (Array.isArray(participations) ? participations : [])
        .filter((rij) => rij && rij.Programme && rij.QuestionnaireID && rij.MomentID && rij.ResearchID)

    if (bruikbaar.length === 0) return null

    return (
        <div className='programme-research-links'>
            {bruikbaar.map((rij) => {
                const language = languageByProgramme[rij.Programme] || 'nl'
                const links = buildQuestionnaireShareLinks({
                    // De omgeving van het PROGRAMMA, niet die van dit lid.
                    client: rij.Programme,
                    questionnaireID: rij.QuestionnaireID,
                    measureMomentID: rij.MomentID,
                    researchID: rij.ResearchID,
                    respondentID: adressID,
                    language,
                })
                if (!links) return null

                return (
                    <div key={`${rij.Programme}:${rij.ResearchID}`} className='household-research-item'>
                        <h3 className='household-research-title'>
                            {rij.ResearchTitle || 'Vragenlijst van het programma'}
                        </h3>
                        <p className='programme-research-moment'>
                            Meting: <strong>{rij.MomentTitle || 'volgens het programma'}</strong>
                        </p>

                        <label className='household-research-field'>
                            <span>Taal</span>
                            <select
                                value={language}
                                onChange={(e) => {
                                    // Waarde eerst uitlezen — `e.target` is het
                                    // levende element en draagt in de updater
                                    // alweer de oude waarde.
                                    // AI-CONTEXT: See docs/pitfalls.md#state-updater-reads-event-target-too-late
                                    const value = e.target.value
                                    setLanguageByProgramme((prev) => ({ ...prev, [rij.Programme]: value }))
                                }}
                            >
                                {languages.map((item) => (
                                    <option key={item.code} value={item.code}>
                                        {item.flag} {item.name}
                                    </option>
                                ))}
                            </select>
                        </label>

                        <div className='household-research-share'>
                            <div className='task-details-activityform-send-container'>
                                <p><b>Link:</b></p>
                                <CopyLink link={links.open} />
                            </div>
                            {qrMode === 'inline' ? (
                                <div className='household-research-qrcode-inline'>
                                    <p><b>QR-code:</b></p>
                                    <QRCode
                                        value={getQrcodeValue({
                                            host: window.location.hostname,
                                            client: rij.Programme,
                                            questionnaire: rij.QuestionnaireID,
                                            measureMomentID: rij.MomentID,
                                            researchID: rij.ResearchID,
                                            persona: adressID || 'undefined',
                                            route: 'research',
                                            languageCode: language,
                                        })}
                                        size={180}
                                    />
                                    <p className='household-research-qrcode-hint'>
                                        Laat de bewoner deze code scannen met zijn eigen telefoon.
                                    </p>
                                </div>
                            ) : (
                                <div className='task-details-activityform-send-container'>
                                    <p><b>QR-code:</b></p>
                                    <Tooltip content='Print de QR-code' top='-40px'>
                                        <img
                                            src={QrcodeIcon}
                                            className='household-research-qrcode-icon'
                                            alt=''
                                            onClick={() => history.push(links.qrcode)}
                                        />
                                    </Tooltip>
                                </div>
                            )}
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

export default ProgrammeResearchLink
